import CheckBoxField, { CheckBoxSuffixField } from '@/components/fields/CheckBoxField';
import DisplayField from '@/components/fields/DisplayField';
import InputField from '@/components/fields/InputField';
import SelectField from '@/components/fields/SelectField';
import ConsultationSaveButtonBanner from '@/components/Patient/components/ConsultationSaveButtonBanner';
import { PrescriptionAssistantTool } from '@/components/Patient/components/PrescriptionAssistantTool/PrescriptionAssistantTool';
import type { PrescriptionAssistantToolDetails } from '@/components/Patient/components/PrescriptionAssistantTool/PrescriptionAssistantTool.types';
import { PatientContext } from '@/components/Patient/PatientContext';
import type { GetConsultationTypeReturn } from '@/components/PatientPageV2/utils/getConsultationType';
import {
  FF_CONSULTATION_CHARGES_UPLIFT,
  FF_ENABLE_INITIAL_DR_PAYMENT,
  FF_ENABLE_SHOPIFY_PAYMENT,
  FF_PATIENT_PAGE_AUTOSAVE_TOUR,
} from '@/constants/featureFlags';
import { ConsultationStatus as ConsultationStatusId } from '@/data/constants';
import { getAuthData, getUser } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import { useCanUserChargeConsult } from '@/hooks/consult/useCanUserChargeConsult';
import useChargeConsult from '@/hooks/consult/useChargeConsult';
import { useConsult } from '@/hooks/consult/useConsult';
import type { SaveConsultationFunc } from '@/hooks/consult/useSaveConsultation';
import type { ConsultationStatusMapped } from '@/hooks/rest/useGetConsultationStatus';
import type { OnSaveConsultationOptions } from '@/pages/Patient';
import { SectionBody } from '@/shared-ui/PageElements/SectionBody';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import type { AuthData, ConsultationPricingTier, ConsultationSubStatusType, DoctorModel, Nurse } from '@/types';
import type { TelecomWaitingRoomEntryStatus } from '@montugroup/pms-api-contracts';
import type { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { Box, Stack, styled } from '@mui/material';
import { debounce } from 'lodash';
import type { EmotionJSX } from 'node_modules/@emotion/react/dist/declarations/types/jsx-namespace';
import type { ChangeEventHandler } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import type { PaymentButtonText } from '../../ConsultationTab.types';
import type { PatientDetails, PrescriberDetails } from '../ConfirmPrescriptionCard/ConfirmPrescriptionCard.types';
import { PrescriptionPadStep } from '../PrescriptionPad';
import { ConsultationDetails } from './ConsultationDetails';
import ConsultationRatePaymentOptions from './ConsultationRatePaymentOptions';
import { ConsultCharge } from './ConsultCharge/ConsultCharge';
import { DoctorConsultActions } from './DoctorConsultActions/DoctorConsultActions';
import { NurseConsultActions } from './NurseConsultActions/NurseConsultActions';
import type { RenderPaymentDetailsProps } from './RenderPaymentDetails';
import RenderPaymentDetails from './RenderPaymentDetails';
import SingleConsultationNoteHistory from './SingleConsultationNotes/components/SingleConsultationNoteHistory';

const StyledSelectField = styled(SelectField)({
  '.MuiSelect-select': {
    minWidth: '250px',
  },
});

interface ConsultationStatus {
  id: number;
  name: string;
}

interface ConsultationType {
  type: string;
  duration_mins: number;
}

export interface ShowConsultation {
  id: number;
  consultation_code: string;
  end_time: string;
  start_time: string;
  doctor_id: number;
  payment_status: any;
  paid_date: string;
  consult_type: string;
  notes: string;
  zoom_url: string;
  status_id: number;
  payment_queued: boolean;
  calcom_consultation_uuid: string;
  xero_consultation_code: string;
  payment_id: string;
  ConsultationStatus: ConsultationStatus;
  ConsultationSubStatus: ConsultationSubStatusType;
  ConsultationType: ConsultationType;
  Doctor: DoctorModel;
  Nurse: Nurse;
  sub_status_id: number;
  calendly_consultation_uuid: string;
  calendly_edit_uuid: string;
  paymentStatus: any;
  waiting_room_status?: TelecomWaitingRoomEntryStatus;
  pricingTier?: ConsultationPricingTier | null;
  isMarkedAsComplimentary?: boolean;
  hasPaidInitialNurseConsult: boolean | null;
}

interface Doctor {
  id: number;
  prescriber_number: string;
  DoctorUser: PrescriberDetails;
}

interface Fields {
  detailsConfirmed: boolean;
  medicalSummary: boolean;
  PaymentVerified: boolean;
  PsychosisHx: boolean;
  psycosis: string;
  DrugDrug: boolean;
  DrugDrugComment: string;
  falls: boolean;
  fallsComment: string;
  SubstanceAbuse: boolean;
  SubstanceAbuseComment: string;
  SiTosh: boolean;
  SiToshComment: string;
  RecentlyIncarcerated: boolean;
  RecentlyIncarceratedComment: string;
  Lactating: boolean;
  LactatingComment: string;
  MCprocess: boolean;
  treatmentGoal: string;
  email: string;
}

interface ShowConsultationProps extends Pick<RenderPaymentDetailsProps, 'handleConcessionPayment'> {
  showConsultation: ShowConsultation;
  setShowConsultation: React.Dispatch<React.SetStateAction<ShowConsultation | null>>;
  handleClose: () => void;
  patientId: string;
  patient: PatientDetails;
  authData: AuthData;
  patientActive: boolean;
  fields: Fields;
  handleChange: (prop: string) => void;
  handleDocNotes: (val: string) => void;
  renderFollowUpConsultationSelect: () => EmotionJSX.Element | null;
  isUserAuthorisedForConsultation?: boolean;
  createScriptStep: PrescriptionPadStep;
  doctor: Doctor;
  isPostingPrescription: boolean;
  statusIsNotPaid: boolean;
  paymentsButtonAction: () => Promise<void>;
  isPaymentButtonDisabled: boolean;
  canShowDiscountRateInput: boolean;
  loadingPaymentAction: boolean;
  handleCancelScript: () => void;
  handleNoShow: () => void;
  nurseOnSave: () => void;
  onSaveConsultation: (params?: OnSaveConsultationOptions) => Promise<void>;
  isComplimentary: boolean;
  handleIsComplimentaryChange: () => void;
  getPaymentButtonText(): PaymentButtonText;
  consultationStatuses: ConsultationStatusMapped[];
  handleStatusChange: (statusId: number) => void;
  consultationSubStatus: ConsultationSubStatusType;
  handleSubStatusChange: (status: ConsultationSubStatusType) => void;
  consultDiscountRate: string;
  handleChangeConsultDiscountRate: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  loadingAnyPayment: boolean;
  handleShopifyPaymentRequest: (paymentRequestType?: PaymentRequestType) => Promise<void>;
  handleCompleteConsultation: () => void;
  isSavingConsultation?: boolean;
  saveConsultation: SaveConsultationFunc;
  prescriptionDetails: PrescriptionAssistantToolDetails;
  onPrescriptionErrorClosed: () => void;
  prescribeStateError: boolean;
  onOnlyCancelComplete: () => Promise<void>;
}

interface PaymentOptionsWrapperProps {
  authData: AuthData;
  consultAndClinicianType: GetConsultationTypeReturn;
  ffEnableShopifyPayment: boolean;
  canUserChargeConsult: boolean;
  showConsultation: ShowConsultation;
  consultPaymentState: any;
  isShopifyPaymentRequestPending: boolean;
  handleCreateConsultationPaymentRequest: (paymentRequestType?: PaymentRequestType) => Promise<void>;
  renderPaymentDetailsProps: RenderPaymentDetailsProps;
}

const PaymentDetailsWrapper = ({
  authData,
  consultAndClinicianType,
  ffEnableShopifyPayment,
  canUserChargeConsult,
  showConsultation,
  consultPaymentState,
  isShopifyPaymentRequestPending,
  handleCreateConsultationPaymentRequest,
  renderPaymentDetailsProps,
}: PaymentOptionsWrapperProps) => {
  const { isDoc } = authData;

  if (isDoc) {
    return null;
  }

  if (!ffEnableShopifyPayment || !canUserChargeConsult) {
    return <RenderPaymentDetails {...renderPaymentDetailsProps} />;
  }

  // render existing payment details if a xero code exists but no shopify pricing tier
  // this is a temporary inbetween state to assist with the transition to the new payment flow
  if (!showConsultation.pricingTier && showConsultation.xero_consultation_code) {
    return <RenderPaymentDetails {...renderPaymentDetailsProps} />;
  }

  return (
    <ConsultationRatePaymentOptions
      authData={authData}
      consultAndClinicianType={consultAndClinicianType}
      paymentStatus={consultPaymentState}
      isPending={isShopifyPaymentRequestPending}
      handlePaymentRequest={handleCreateConsultationPaymentRequest}
      pricingTier={showConsultation?.pricingTier}
      consultation={showConsultation}
    />
  );
};

const ShowConsultation = ({
  showConsultation,
  handleClose,
  patientId,
  patient,
  authData,
  patientActive,
  fields,
  handleChange,
  handleDocNotes,
  renderFollowUpConsultationSelect,
  isUserAuthorisedForConsultation,
  createScriptStep,
  doctor,
  isPostingPrescription,
  statusIsNotPaid,
  paymentsButtonAction,
  isPaymentButtonDisabled,
  getPaymentButtonText,
  canShowDiscountRateInput,
  loadingPaymentAction,
  handleNoShow,
  handleCancelScript,
  handleCompleteConsultation,
  nurseOnSave,
  onSaveConsultation,
  isComplimentary,
  handleIsComplimentaryChange,
  consultationStatuses,
  handleStatusChange,
  consultationSubStatus,
  handleSubStatusChange,
  consultDiscountRate,
  handleChangeConsultDiscountRate,
  handleConcessionPayment,
  loadingAnyPayment,
  handleShopifyPaymentRequest,
  isSavingConsultation,
  saveConsultation,
  prescriptionDetails,
  onPrescriptionErrorClosed,
  prescribeStateError,
  onOnlyCancelComplete,
}: ShowConsultationProps) => {
  const { isDoc } = authData;

  const user = getUser();
  const { isManager, isAdmin } = getAuthData();
  const { flags } = useFeatureFlags();
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];
  const ffConsultationChargesUplift = flags[FF_CONSULTATION_CHARGES_UPLIFT];
  const ffEnableShopifyPayment = flags[FF_ENABLE_SHOPIFY_PAYMENT];
  const ffAutosaveTour = flags[FF_PATIENT_PAGE_AUTOSAVE_TOUR];
  const currentConsultPaymentDetails = showConsultation?.paymentStatus || {};
  const { paymentMethod: consultPaymentMethod, paymentState: consultPaymentState } = useConsult({
    paymentStatus: showConsultation?.payment_status,
    paymentDetails: currentConsultPaymentDetails,
    paidDate: showConsultation?.paid_date ?? '',
    paymentQueued: showConsultation?.payment_queued,
    invoiceCode: showConsultation?.xero_consultation_code,
  });
  const { email } = useContext(PatientContext);
  const { formattedConcessionConsulationPrice, discountInputProps } = useChargeConsult({
    consultationId: showConsultation?.id,
    consultationType: showConsultation?.consult_type as 'Initial' | 'Follow-up' | undefined,
    doctorId: showConsultation?.doctor_id,
    patientEmail: email,
    consultationPaymentMethod: consultPaymentMethod,
  });

  const { canUserChargeConsult, consultAndClinicianType } = useCanUserChargeConsult({
    consultationType: showConsultation?.consult_type as 'Initial' | 'Follow-up' | undefined,
    doctorId: showConsultation?.doctor_id,
  });

  const [notes, setNotes] = useState(showConsultation.notes);

  const handleDocNotesDebounced = useCallback(
    debounce((value) => {
      handleDocNotes(value);
    }, 500),
    [],
  );

  const handleOnlyCancelComplete = useCallback(async () => {
    await onOnlyCancelComplete();
  }, [onOnlyCancelComplete]);

  const showDoctorActions =
    showConsultation.doctor_id && (showConsultation.Doctor.DoctorUser.id === user?.id || isAdmin || isManager);
  const showNurseActions = !showDoctorActions;
  const isConsultationReScheduled = showConsultation.ConsultationStatus.id === ConsultationStatusId.re_scheduled;

  const modifiedConsultData = useMemo(
    () => ({
      ...showConsultation,
      ConsultationSubStatus: consultationSubStatus,
      sub_status_id: consultationSubStatus?.id,
    }),
    [showConsultation, consultationSubStatus],
  );

  return (
    <>
      {ffAutosaveTour && <ConsultationSaveButtonBanner />}
      <ConsultationDetails
        consultation={showConsultation}
        onClose={handleClose}
        patientId={patientId}
        patientPhoneNumber={patient?.phone}
        patientName={`${patient.first_name ?? ''} ${patient.last_name ?? ''}`}
      />
      {ffEnableInitialDrPayment ? (
        <ConsultCharge
          variant="nurse"
          consultation={showConsultation}
          isConcessionPatient={patient.hasValidConcessionCard}
        />
      ) : (
        <PaymentDetailsWrapper
          authData={authData}
          ffEnableShopifyPayment={ffEnableShopifyPayment}
          canUserChargeConsult={canUserChargeConsult}
          consultAndClinicianType={consultAndClinicianType}
          showConsultation={showConsultation}
          consultPaymentState={consultPaymentState}
          isShopifyPaymentRequestPending={loadingAnyPayment}
          handleCreateConsultationPaymentRequest={handleShopifyPaymentRequest}
          renderPaymentDetailsProps={{
            authData,
            statusIsNotPaid,
            getPaymentButtonText,
            isPaymentButtonDisabled,
            paymentsButtonAction,
            showConsultation,
            canShowDiscountRateInput,
            loadingPaymentAction,
            handleConcessionPayment,
            consultDiscountRate,
            handleChangeConsultDiscountRate,
            formattedConcessionConsulationPrice,
            discountInputProps,
            canUserChargeConsult,
            loadingChargeConsult: loadingAnyPayment,
            loadingChargeConcession: loadingAnyPayment,
            ffConsultationChargesUplift,
          }}
        />
      )}
      {(!isDoc || showConsultation?.Nurse?.id) && (
        <Box component="section">
          <SectionHeader headerText={showConsultation.doctor_id ? 'Treatment Plan' : 'Patient Assessment'} />
          <SectionBody>
            {!showConsultation.doctor_id && (
              <>
                <Box mt={0.5} width="100%">
                  <CheckBoxField
                    id="patient-details-confirmation"
                    label="Patient Details"
                    placeholder="-"
                    disabled={!patientActive}
                    labelPadding="11px 7px 7px 0px"
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    labelFontWeight={900}
                    checked={fields.detailsConfirmed}
                    InputClasses="custom-adress-field"
                    onChange={handleChange('detailsConfirmed')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box p={1} mt={1} ml={2.75} fontSize={12} fontStyle="italic">
                        Confirmed
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.5} width="100%" height="40px">
                  <CheckBoxField
                    id="medical-summary-confirmation"
                    label="Medical Summary"
                    placeholder="-"
                    disabled={!patientActive}
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={900}
                    checked={fields.medicalSummary}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    InputClasses="custom-adress-field"
                    onChange={handleChange('medicalSummary')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box p={1} mt={1} ml={2.75} fontSize={12} fontStyle="italic">
                        Verified
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.5} width="100%" height="40px">
                  <CheckBoxField
                    id="payment-confirmation"
                    label="Payment"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={900}
                    disabled={!patientActive}
                    checked={fields.PaymentVerified || showConsultation.payment_status === 'PAID'}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    InputClasses="custom-adress-field"
                    onChange={handleChange('PaymentVerified')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box>
                        <Box
                          style={{ display: 'inline-block' }}
                          p={1}
                          mt={1}
                          ml={2.75}
                          fontSize={12}
                          fontStyle="italic"
                        >
                          Verified
                        </Box>
                      </Box>
                    }
                  />
                </Box>
                <Box mt={1}>
                  <DisplayField
                    id="risk-screening"
                    label="Risk Assessment"
                    fieldText={
                      <Box fontSize={12} ml={2}>
                        Comments
                      </Box>
                    }
                    margin={0}
                    labelPadding="0px"
                    fieldPadding="0px"
                  />
                </Box>
                <Box mt={0.5} width="100%">
                  <CheckBoxField
                    id="medical-Psychosis-Hx"
                    label="Psychosis History"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={500}
                    disabled={!patientActive}
                    checked={fields.PsychosisHx}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    InputClasses="custom-adress-field"
                    onChange={handleChange('PsychosisHx')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="psycosis-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.PsychosisHx}
                          value={fields.psycosis}
                          onChange={handleChange('psycosis')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.25} width="100%">
                  <CheckBoxField
                    id="drug-drug"
                    label="Drug-Drug Interaction"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={500}
                    checked={fields.DrugDrug}
                    disabled={!patientActive}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    InputClasses="custom-adress-field"
                    onChange={handleChange('DrugDrug')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="DrugDrugComment-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.DrugDrug}
                          value={fields.DrugDrugComment}
                          onChange={handleChange('DrugDrugComment')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.25} width="100%">
                  <CheckBoxField
                    id="falls"
                    label="Falls Risk"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    disabled={!patientActive}
                    labelFontWeight={500}
                    checked={fields.falls}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    InputClasses="custom-adress-field"
                    onChange={handleChange('falls')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="fallsComment-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.falls}
                          value={fields.fallsComment}
                          onChange={handleChange('fallsComment')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.25} width="100%">
                  <CheckBoxField
                    id="substance_abuse"
                    label="Substance Abuse"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={500}
                    checked={fields.SubstanceAbuse}
                    disabled={!patientActive}
                    InputClasses="custom-adress-field"
                    onChange={handleChange('SubstanceAbuse')}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="SubstanceAbuseComment-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.SubstanceAbuse}
                          value={fields.SubstanceAbuseComment}
                          onChange={handleChange('SubstanceAbuseComment')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.25} width="100%">
                  <CheckBoxField
                    id="recently_incarcerated"
                    label="Recently Incarcerated"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={500}
                    disabled={!patientActive}
                    checked={fields.RecentlyIncarcerated}
                    InputClasses="custom-adress-field"
                    onChange={handleChange('RecentlyIncarcerated')}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="RecentlyIncarceratedComment-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.RecentlyIncarcerated}
                          value={fields.RecentlyIncarceratedComment}
                          onChange={handleChange('RecentlyIncarceratedComment')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.25} width="100%">
                  <CheckBoxField
                    id="si_tosh"
                    label="SI/TOSH"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={500}
                    checked={fields.SiTosh}
                    InputClasses="custom-adress-field"
                    disabled={!patientActive}
                    onChange={handleChange('SiTosh')}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="SiToshComment-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.SiTosh}
                          value={fields.SiToshComment}
                          onChange={handleChange('SiToshComment')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={0.25} width="100%">
                  <CheckBoxField
                    id="Lactating"
                    label="Pregnant OR Lactating"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={500}
                    disabled={!patientActive}
                    checked={fields.Lactating}
                    InputClasses="custom-adress-field"
                    onChange={handleChange('Lactating')}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        mt={0.5}
                        border={1}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box ml={4} p={0.25} width="100%">
                        <CheckBoxSuffixField
                          id="LactatingComment-field-suffix"
                          type="text"
                          placeholder="-"
                          disabled={!fields.Lactating}
                          value={fields.LactatingComment}
                          onChange={handleChange('LactatingComment')}
                        />
                      </Box>
                    }
                  />
                </Box>
                <Box mt={2} width="100%">
                  <StyledSelectField
                    id="treatment-goal"
                    label="Treatment Goal"
                    disabled={!patientActive}
                    options={[
                      {
                        name: 'Patient wants to add cannabis to current regime',
                        value: 'Patient wants to add cannabis to current regime',
                      },
                      {
                        name: 'Patient wants to/has stopped using other medication',
                        value: 'Patient wants to/has stopped using other medication',
                      },
                      {
                        name: 'Patient wants to reduce dosages of their current medication',
                        value: 'Patient wants to reduce dosages of their current medication',
                      },
                    ]}
                    labelPadding="7px 5px 5px 0px"
                    value={fields.treatmentGoal}
                    onChange={handleChange('treatmentGoal')}
                    varient="outlined"
                    margin={0}
                  />
                </Box>
                <Box mt={1} width="100%">
                  <CheckBoxField
                    id="mc-process"
                    label="MC Process"
                    placeholder="-"
                    labelPadding="10px 7px 7px 0px"
                    disabled={!patientActive}
                    icon={
                      <Box
                        width="17px"
                        height="17px"
                        ml={0.5}
                        border={1}
                        mr={0}
                        borderRadius="2px"
                        borderColor="#c4c4c4"
                      />
                    }
                    labelFontWeight={900}
                    checked={fields.MCprocess}
                    InputClasses="custom-adress-field"
                    onChange={handleChange('MCprocess')}
                    customGrid={[3, 9]}
                    margin={0}
                    checkBoxSuffix={
                      <Box p={1} mt={0.5} ml={3} fontSize={12} fontStyle="italic">
                        Explained
                      </Box>
                    }
                  />
                </Box>
              </>
            )}
          </SectionBody>
        </Box>
      )}
      <Stack spacing={2} mb={4}>
        <SectionHeader headerText="Consultation Notes" />
        <InputField
          id="notes"
          type="text"
          placeholder="Notes:"
          customGrid={[12, 12]}
          display="block"
          multiline
          labelPadding="5px 0px 2px 0px"
          disabled={!patientActive}
          marginBottom={0}
          InputClasses="textarea-h-100-px"
          value={notes}
          onChange={(e: { target: { value: any } }) => {
            setNotes(e.target.value);
            handleDocNotesDebounced(e.target.value);
          }}
        />
        {isDoc && renderFollowUpConsultationSelect()}
        <SingleConsultationNoteHistory consultationId={showConsultation.id} />
      </Stack>
      {!ffEnableInitialDrPayment && isDoc && (
        <RenderPaymentDetails
          authData={authData}
          statusIsNotPaid={statusIsNotPaid}
          getPaymentButtonText={getPaymentButtonText}
          isPaymentButtonDisabled={isPaymentButtonDisabled}
          paymentsButtonAction={paymentsButtonAction}
          showConsultation={showConsultation}
          canShowDiscountRateInput={canShowDiscountRateInput}
          loadingPaymentAction={loadingPaymentAction}
          handleConcessionPayment={handleConcessionPayment}
          consultDiscountRate={consultDiscountRate}
          handleChangeConsultDiscountRate={handleChangeConsultDiscountRate}
          formattedConcessionConsulationPrice={formattedConcessionConsulationPrice}
          discountInputProps={discountInputProps}
          canUserChargeConsult={canUserChargeConsult}
          loadingChargeConsult={loadingAnyPayment}
          loadingChargeConcession={loadingAnyPayment}
          ffConsultationChargesUplift={ffConsultationChargesUplift}
        />
      )}
      {isUserAuthorisedForConsultation && (
        <Stack mt={3}>
          <SectionHeader
            data-testid="prescription"
            headerText={createScriptStep === PrescriptionPadStep.Create ? 'Prescriptions' : 'Confirm Prescription'}
            sx={{ marginBottom: '1rem' }}
          />

          {doctor && isDoc && (
            <SectionBody paddingX={0} paddingTop={4}>
              <PrescriptionAssistantTool
                prescriptionDetails={prescriptionDetails}
                isPostingPrescription={isPostingPrescription}
                showPrescribeStateError={prescribeStateError}
                onPrescriptionErrorClosed={onPrescriptionErrorClosed}
                onOnlyCancelComplete={handleOnlyCancelComplete}
              />
            </SectionBody>
          )}
          {!isDoc && renderFollowUpConsultationSelect()}
        </Stack>
      )}
      <Box>
        {ffEnableInitialDrPayment && (
          <ConsultCharge
            variant="doctor"
            consultation={showConsultation}
            isConcessionPatient={patient.hasValidConcessionCard}
          />
        )}
        {showDoctorActions && !isConsultationReScheduled && (
          <DoctorConsultActions
            showConsultation={modifiedConsultData}
            patientActive={patientActive}
            isComplimentary={isComplimentary}
            loadingPaymentAction={loadingPaymentAction}
            handleIsComplimentaryChange={handleIsComplimentaryChange}
            handleCancelScript={handleCancelScript}
            handleNoShow={handleNoShow}
            handleSaveConsultation={onSaveConsultation}
            handleResendInvoice={paymentsButtonAction}
            handleShopifyPayment={handleShopifyPaymentRequest}
            handleCompleteConsultation={handleCompleteConsultation}
            isSavingConsultation={isSavingConsultation}
            consultationStatuses={consultationStatuses}
            handleSubmit={saveConsultation}
            handleSubStatusChange={handleSubStatusChange}
          />
        )}
        {showNurseActions && (
          <NurseConsultActions
            showConsultation={modifiedConsultData}
            patientActive={patientActive}
            consultationStatuses={consultationStatuses}
            handleSubmit={nurseOnSave}
            handleSaveConsultation={onSaveConsultation}
            handleStatusChange={handleStatusChange}
            handleSubStatusChange={handleSubStatusChange}
          />
        )}
      </Box>
    </>
  );
};

export { ShowConsultation };
